/**
 * Запись осмотра.
 *
 * Правило 1: append-only, ни update, ни delete.
 * Правило 2: шаблон вмораживается в запись — но снапшот строит СЕРВЕР,
 *   разворачивая закреплённую версию шаблона. Клиент присылает только
 *   `templateKey` + `templateVersion` + `locale`. Иначе подменённый клиент
 *   прислал бы чек-лист из одного пункта и «прошёл» осмотр.
 * Правило 5: время клиента и сервера пишутся оба, расхождение помечается.
 * Правило 6: у каждого пункта своё время выполнения — разрывы должны быть видны.
 */
import { z } from 'zod';
import { localeSchema, objectIdSchema } from './common.js';
export const gpsSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    accuracyMeters: z.number().min(0).max(100_000),
});
export const sha256Schema = z.string().regex(/^[a-f\d]{64}$/i);
/**
 * Ссылка на уже загруженный в хранилище объект.
 *
 * `versionId` обязателен для доказательной ценности: Object Lock защищает
 * ВЕРСИИ, а не имена. Поверх объекта всегда можно положить новую версию —
 * старая останется нетронутой и WORM-защищённой, но чтение «последней»
 * вернёт подделку. Поэтому запись закрепляет конкретную версию.
 */
export const blobRefSchema = z.object({
    objectKey: z.string().min(1).max(512),
    versionId: z.string().min(1).max(128).nullable().default(null),
    sha256: sha256Schema,
    bytes: z.number().int().min(1).max(25 * 1024 * 1024),
});
export const ITEM_VERDICTS = ['ok', 'warn', 'critical', 'na'];
export const itemResultSchema = z.object({
    /** Идентификатор развёрнутого пункта: `tread_depth@steer_1_left`. */
    id: z.string().min(1).max(160),
    /** Отметка «неприменимо» — например, у юнита нет этого узла. */
    skipped: z.boolean().default(false),
    boolValue: z.boolean().nullable().default(null),
    measurementValue: z.number().nullable().default(null),
    photos: z.array(blobRefSchema).max(6).default([]),
    audio: blobRefSchema.nullable().default(null),
    note: z.string().max(2000).nullable().default(null),
    /** Водитель явно пометил узел как дефектный. */
    defectReported: z.boolean().default(false),
    capturedAtClient: z.coerce.date(),
    gps: gpsSchema.nullable().default(null),
});
export const submitInspectionSchema = z.object({
    /**
     * Идемпотентность: очередь ретраит, сеть моргает между «сервер принял»
     * и «клиент узнал». Без этого через месяц пилота в базе дубли.
     */
    clientUuid: z.uuid(),
    unitId: objectIdSchema,
    templateKey: z.string().min(1).max(64),
    templateVersion: z.number().int().positive(),
    /** Язык, на котором водитель видел чек-лист. Вмораживается в снапшот. */
    locale: localeSchema,
    startedAtClient: z.coerce.date(),
    submittedAtClient: z.coerce.date(),
    gps: gpsSchema.nullable().default(null),
    items: z.array(itemResultSchema).min(1).max(400),
});
export const INSPECTION_VERDICTS = ['pass', 'defects', 'critical'];
export const INSPECTION_STATUSES = ['pending', 'complete'];
export const presignUploadSchema = z.object({
    unitId: objectIdSchema,
    sha256: sha256Schema,
    kind: z.enum(['photo', 'audio']),
    extension: z.string().min(1).max(8).regex(/^[a-z0-9]+$/i),
    bytes: z.number().int().min(1).max(25 * 1024 * 1024),
});
export const presignUploadResponseSchema = z.object({
    url: z.string(),
    objectKey: z.string(),
    expiresAt: z.coerce.date(),
});
/* ------------------------------- дефекты --------------------------------- */
export const DEFECT_SEVERITIES = ['minor', 'major', 'critical'];
/**
 * Цикл дефекта. Ключевой шаг — `acknowledged`: следующий водитель обязан
 * увидеть незакрытый дефект до начала своего осмотра и подтвердить.
 * Это и безопасность, и сильнейшее доказательство: видно, что информация
 * дошла до человека, а не осталась в системе.
 */
/**
 * Состояния дефекта.
 *
 * `verified` убрано: оно было объявлено и недостижимо, а объявленное состояние
 * без перехода вводит в заблуждение сильнее, чем его отсутствие. Проверка
 * ремонта — это и есть закрытие: механик выкладывает фото выполненной работы
 * (`repaired`), другой человек его принимает (`closed`). Совмещение этих ролей
 * остаётся предупреждением, а не запретом.
 */
export const DEFECT_STATUSES = [
    'open',
    'acknowledged',
    'assigned',
    'repaired',
    'closed',
];
/**
 * Назначение дефекта исполнителю.
 *
 * Состояние `assigned` было объявлено в DEFECT_STATUSES с самого начала, но
 * недостижимо: перехода не существовало, и цикл обрывался на подтверждении
 * следующим водителем. Без него право `defect:assign` не имело эндпоинта,
 * а механик не мог взять дефект в работу.
 */
export const assignDefectSchema = z.object({
    assigneeUserId: objectIdSchema,
    note: z.string().max(2000).nullable().default(null),
});
/** Отметка о выполненном ремонте. Фото обязательно — иначе это та же галочка. */
export const repairDefectSchema = z.object({
    photos: z.array(blobRefSchema).min(1),
    note: z.string().max(2000).nullable().default(null),
});
export const acknowledgeDefectsSchema = z.object({
    unitId: objectIdSchema,
    defectIds: z.array(objectIdSchema).min(1).max(100),
});
/** Закрытие только с фото выполненной работы — иначе это та же галочка. */
export const closeDefectSchema = z.object({
    photos: z.array(blobRefSchema).min(1).max(6),
    note: z.string().max(2000).nullable().default(null),
});
/** Расхождение часов, при котором клиентское время считается недостоверным. */
export const CLOCK_SKEW_WARN_MS = 5 * 60_000;
export function clockSkew(clientAt, serverAt) {
    const skewMs = clientAt.getTime() - serverAt.getTime();
    return { skewMs, suspect: Math.abs(skewMs) > CLOCK_SKEW_WARN_MS };
}
//# sourceMappingURL=inspection.js.map